const roundAmount = (amount) => {
  const num = Number(amount);
  if (isNaN(num)) return 0;
  const fractional = num % 1;
  if (fractional >= 0.5) return Math.ceil(num);
  return num;
};

const extractFeeParts = (note) => {
  if (!note) {
    return { percentage: "\u2014", feeAmount: "\u2014" };
  }

  const percentageMatch = note.match(/(\d+(?:\.\d+)?)\s*%/);
  const amountMatch = note.match(/₹\s*([\d,]+(?:\.\d+)?)/i);

  return {
    percentage: percentageMatch ? `${percentageMatch[1]}%` : "\u2014",
    feeAmount: amountMatch ? `\u20B9${Number(amountMatch[1].replace(/,/g, "")).toLocaleString("en-IN")}` : "\u2014",
  };
};

const formatDescription = (label) => {
  if (!label) return "\u2014";

  if (/commission/i.test(label)) return "Commission";
  if (/milestone/i.test(label)) return "Milestone";

  return label;
};

export const PDF_OPTIONS = {
  margin: 0.4,
  filename: 'payment-report.pdf',
  image: { type: 'jpeg', quality: 1 },
  html2canvas: { scale: 3, useCORS: true },
  jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
};

export const REPORT_INFO = {
  customerName: 'John Doe',
  transactionNo: 'TXN-984723',
  date: '09 May 2026',
  totalAmount: '$1,850.00',
};

export const TABLE_ITEMS = [
  { item: 'Visa Consultation', description: 'Professional consultation session', qty: 1, price: 500 },
  { item: 'Mock Interview', description: 'AI interview preparation service', qty: 1, price: 350 },
  { item: 'Application Review', description: 'Complete profile and document review', qty: 2, price: 500 },
];

export const TOTALS = {
  subtotal: 1850,
  tax: 0,
  total: 1850,
};

export function generateEarningReportHTML({ transactions, userName, fromDate, toDate, reportLabel, logoSrc, availableBalance }) {
  const filtered = transactions.filter((t) => {
    const d = new Date(t.date);
    if (fromDate && d < new Date(fromDate)) return false;
    if (toDate && d > new Date(toDate + "T23:59:59")) return false;
    return true;
  });

  const rows = filtered
    .map(
      (t) => {
        const { percentage, feeAmount } = extractFeeParts(t.note);

        return `
    <tr>
      <td class="report-td">${new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</td>
      <td class="report-td">${formatDescription(t.label)}</td>
      <td class="report-td">${t.country || "\u2014"}</td>
      <td class="report-td">${percentage}</td>
      <td class="report-td">${feeAmount}</td>
      <td class="report-td">
        ${t.type === "credit" ? `${Math.abs(t.amount).toLocaleString("en-IN")}` : "\u2014"}
      </td>
    </tr>`;
      }
    )
    .join("");

  const dateRange =
    fromDate || toDate
      ? `${fromDate ? new Date(fromDate).toLocaleDateString("en-IN") : "All"} to ${toDate ? new Date(toDate).toLocaleDateString("en-IN") : "Today"}`
      : "All Time";

  const logoHtml = logoSrc
    ? `<img src="${logoSrc}" alt="TheVisaCo" style="width:100%;max-height:120px;object-fit:contain;object-position:left top;" />`
    : `<div style="font-size:22px;font-weight:700;color:#000000;">TheVisaCo</div>`;

  const totalCredits = filtered.filter(t => t.type === "credit").reduce((s, t) => s + t.amount, 0);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${reportLabel} \u2014 TheVisaCo</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;font-family:'Inter','Segoe UI',sans-serif;}
  body{background:#eef2f7;padding:0 20px 20px;color:#000000;}
  .report-container{max-width:1040px;margin:auto;}
  .report-card{background:rgba(255,255,255,0.72);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-radius:32px;padding:4px 32px 28px;box-shadow:0 10px 40px rgba(15,23,42,0.06),inset 0 1px 0 rgba(255,255,255,0.65);}
  .report-top-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;gap:24px;}
  .report-left-side{display:flex;flex-direction:column;gap:0;flex-shrink:0;}
  .report-logo-box{width:300px;min-height:124px;display:flex;align-items:center;justify-content:flex-start;overflow:hidden;}
  .report-logo-box img{width:100%;max-height:124px;object-fit:contain;object-position:left top;}
  .report-right-side{display:flex;flex-direction:column;align-items:flex-end;gap:12px;flex:0 0 auto;}
  .report-label{padding:10px 18px;border-radius:14px;background:rgba(255,255,255,0.6);font-size:12px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#000000;text-align:center;}
  .report-info-card{width:min(100%,420px);background:rgba(255,255,255,0.96);border-radius:24px;padding:16px 20px;border:1px solid rgba(148,163,184,0.45);box-shadow:0 12px 28px rgba(15,23,42,0.08);}
  .report-info-card-right{margin-top:0;}
  .report-info-grid{display:grid;grid-template-columns:auto minmax(0,1fr);row-gap:8px;column-gap:12px;}
  .report-info-grid > *:nth-last-child(n + 3){padding-bottom:8px;border-bottom:1px solid rgba(226,232,240,0.85);}
  .report-info-title{font-size:13px;color:#000000;font-weight:400;text-align:left;}
  .report-info-value{font-size:14px;color:#000000;font-weight:500;text-align:left;word-break:break-word;overflow-wrap:break-word;}
  .report-table-wrapper{margin-top:16px;overflow:hidden;border-radius:28px;background:rgba(255,255,255,0.5);box-shadow:0 5px 25px rgba(0,0,0,0.04);}
  .report-table{width:100%;border-collapse:collapse;}
  .report-thead{background:rgba(248,250,252,0.95);}
  .report-th{padding:12px 16px;text-align:center;font-size:12px;font-weight:500;text-transform:uppercase;letter-spacing:1px;color:#000000;border-left:1px solid rgba(226,232,240,0.75);border-right:1px solid rgba(226,232,240,0.75);}
  .report-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:14px;font-weight:300;color:#000000;text-align:center;}
  .report-tbody tr:hover{background:rgba(248,250,252,0.55);}
  .report-total-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:15px;font-weight:700;color:#000000;text-align:center;background:rgba(248,250,252,0.95);}
  .report-total-amount{white-space:nowrap;}
  .report-footer{margin-top:20px;border-top:1px solid rgba(226,232,240,0.7);padding-top:12px;display:flex;justify-content:space-between;font-size:13px;color:#000000;font-weight:300;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff;padding:0;}.report-container{max-width:none;}.report-card{padding:4px 16px 16px;box-shadow:none;border-radius:0;}.report-table-wrapper,.report-total-card{box-shadow:none;}.report-top-header{margin-bottom:10px;gap:18px;}.report-logo-box{width:250px;min-height:100px;}.report-logo-box img{max-height:100px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-table-wrapper{margin-top:12px;border-radius:0;}.report-th{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-total-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-footer{margin-top:16px;padding-top:10px;}.report-info-card{width:360px;padding:12px 16px;box-shadow:none;}.report-info-value{word-break:break-word;overflow-wrap:break-word;}.report-info-grid{row-gap:6px;column-gap:8px;}}
  @media(max-width:768px){body{padding:10px 14px 16px;}.report-card{padding:10px 16px 16px;}.report-top-header{flex-direction:row;gap:16px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-info-card{width:280px;}.report-total-card{width:100%;}}
</style>
</head>
<body>
<div class="report-container">
  <div class="report-card">
    <div class="report-top-header">
      <div class="report-left-side">
        <div class="report-logo-box">${logoHtml}</div>
      </div>
      <div class="report-right-side">
        <div class="report-label">${reportLabel}</div>
        <div class="report-info-card report-info-card-right">
          <div class="report-info-grid">
            <div class="report-info-title">User</div>
            <div class="report-info-value">${userName}</div>
            <div class="report-info-title">Period</div>
            <div class="report-info-value">${dateRange}</div>
            <div class="report-info-title">Date Generated</div>
            <div class="report-info-value">${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div>
            <div class="report-info-title">Transactions</div>
            <div class="report-info-value">${filtered.length}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="report-table-wrapper">
      <table class="report-table">
        <thead class="report-thead">
          <tr>
            <th class="report-th">Date</th>
            <th class="report-th">Description</th>
            <th class="report-th">Country</th>
            <th class="report-th">Percentage</th>
            <th class="report-th">Fee</th>
            <th class="report-th">Amount</th>
          </tr>
        </thead>
        <tbody class="report-tbody">
          ${rows || `<tr><td class="report-td" colspan="6" style="text-align:center;font-style:italic;">No transactions in this period</td></tr>`}
          <tr>
            <td class="report-total-td" colspan="5" style="text-align:right;text-transform:uppercase;letter-spacing:0.5px;">Totals</td>
            <td class="report-total-td report-total-amount" style="color:#16a34a;">${roundAmount(totalCredits).toLocaleString("en-IN")}</td>
          </tr>

        </tbody>
      </table>
    </div>

    <div class="report-footer">
      <span>TheVisaCo &mdash; Confidential Report</span>
      <span>&copy; ${new Date().getFullYear()} TheVisaCo &mdash; support@thevisaco.com</span>
    </div>
  </div>
</div>
<script>
window.onafterprint = function() { window.close(); };
window.onload = function() {
  window.print();
  setTimeout(function() { window.close(); }, 1200);
};
window.onfocus = function() {
  setTimeout(function() { window.close(); }, 300);
};
<\/script>
</body>
</html>`;
}
